import { useCallback, useEffect, useMemo, useState } from "react";
import { ButtonTypes, ListData_ValueTypes, NewFieldStyles, TableStyles_Types } from "../../../../Definitions";
import { colors, css, fonts, frames, glyphs, mediaQueriesNumbers, useMediaQuery } from "../../../../Styles";
import NewButton from "../../../../Components/BaseComponents/Buttons/newButton";
import { NewInput, PageHeader, LoadingSpinner, TableLayout, SecondaryPopover } from "../../../../Components";
import { useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, getPaginationRowModel } from "@tanstack/react-table";
import { GenericDataParameters, GenericRow, useColumnConstructor } from "../../../../Components/Tables/_Tables/useColumnConstructor";
import { ReactiveTable } from "../../../../Components/Tables/reactiveTableRenderer";
import globalFilter from "../../../../Components/Tables/_Tables/useGlobalFilter";
import { debounce } from "lodash";
import { useLazyQuery } from "@apollo/client";
import { ExtendedCompaniesResponse, ExtendedCompaniesArgs, EXTENDED_COMPANIES } from "../../../../Services/Agrisys_GqlDataCaller/ServiceToolCalls/GQLCalls/extendedCompanies";
import { useTranslation } from "react-i18next";

const columnsData: GenericDataParameters<GenericRow>[] = [
	{ headerValue: "ID", accessor: "id", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "domain", accessor: "domain", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "name", accessor: "name", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "tags", accessor: "tags", type: ListData_ValueTypes.Div, filter: false },
	{ headerValue: "pin", accessor: "pin", type: ListData_ValueTypes.Number, sort: true },
	{ headerValue: "vpn address", accessor: "vpn_address", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "city", accessor: "city", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "street", accessor: "street", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "phone", accessor: "phone", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "vat", accessor: "vat", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "email", accessor: "email", type: ListData_ValueTypes.String, sort: true },
	{ headerValue: "legacy", accessor: "legacy", type: ListData_ValueTypes.Check, sort: true },
	{ headerValue: "version", accessor: "versionName", type: ListData_ValueTypes.String, sort: true },
]

export default function Companies() {
	const mobileXs = useMediaQuery(`(max-width: ${mediaQueriesNumbers.mobileXs}px)`);
	
	const { t } = useTranslation();
	const limit = 50;

	const [pagination, setPagination] = useState({
		pageIndex: 0, //initial page index
		pageSize: limit,
	});

	const [globalFilterValue, setGlobalFilterValue] = useState<string>("");

	const [fetchData, { data: data, loading: loading, error: error, }] = useLazyQuery<ExtendedCompaniesResponse, ExtendedCompaniesArgs>(EXTENDED_COMPANIES, {
		fetchPolicy: "network-only",
		context: { headers: {tocloud: true}}
	});

	useEffect(() => {
		setPagination({ pageIndex: 0, pageSize: limit });
		fetchData();
	}, []);

	const tableData: GenericRow[] = useMemo(() => {
		const companies = data?.user.extendedCompanies.map(company => {
			return {
				...company,
				id: company.company_id,
				tags: company.tags && (
					<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
						<SecondaryPopover
							content={<glyphs.Note fillColor={colors.greyDarkPrimary} />}
							popoverHeader={"Tags"}
							popoverContent={
								<div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", ...fonts.paragraph, ...fonts.fontSize4, color: colors.greyLightTertiary, minHeight: "15px" }}>
									{company.tags.map((tag, i) => <p key={i} >{tag}</p>)}
								</div>
							}
						/>
					</div>
				),
				version: undefined,
				versionName: company.version?.version
			}
		});
		return companies ?? [];
	}, [data]);

	const columns = useColumnConstructor({
		accessorData: columnsData,
		tableStyle: TableStyles_Types.primaryTable,
		canEdit: false,
	});

	const table = useReactTable({
		columns,
		data: tableData,
		getCoreRowModel: getCoreRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		onPaginationChange: setPagination,
		globalFilterFn: globalFilter,
		state: {
			globalFilter: globalFilterValue,
			pagination,
		},
	});

	const debouncedSearch = useCallback(
		debounce((value: string) => {
			setGlobalFilterValue(value);
		}, 500),
		[]
	);

	return (
		<frames.Page_inner>
				<PageHeader topHeader='Companies' subHeader='Overview' />
			<frames._page_inner_content>
				<TableLayout
					fillScreenTable
					searchbBarArea={
						<NewInput
							onChange={debouncedSearch}
							fieldOverrides={{
								fieldStyle: NewFieldStyles.greySecondary,
								fieldWidth: mobileXs ? "150px" : undefined
							}}
							placeholder={t("search", { defaultValue: "Search" })}
							isClearable
						/>
					}
					buttonArea={
						<NewButton
							content={"fetch"}
							buttonType={ButtonTypes.tableButton}
							startEnhancer={glyphs.Fetch({})}
							onClick={() => {
								fetchData();
							}}
							disabled={loading}
						/>
					}
						tableArea={<>
							{loading && <div style={{padding: "20px", display: "flex", justifyContent: "center"}}><LoadingSpinner/></div>}
							{(!loading && (error ||tableData.length === 0)) && <p style={{ ...css.emptyPen, textAlign: "center", padding: "20px" }}>{t("noData", { defaultValue: "no data" })}</p>}
							{!loading && !error && tableData.length !== 0 &&
								<ReactiveTable
									table={table}
									tableType={TableStyles_Types.primaryTable}
									emptyText={t("noData", { defaultValue: "no data" })}
									renderFooter={false}
								/>
							}
						</>}
						paginationBtns={{
							onPrev: () => { if (table.getCanPreviousPage()) {table.previousPage()} },
							onNext: () => { if (table.getCanNextPage()) {table.nextPage()} },
							canNextPage: !table.getCanNextPage(),
							canPreviousPage: !table.getCanPreviousPage(),
							thisPageIndex: table.getState().pagination.pageIndex + 1,
							pageCount: table.getPageCount()
						}}
				/>
			</frames._page_inner_content>
		</frames.Page_inner>
	)
}